import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, ArrowLeft, PackageX, Search } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { productAPI } from '../services/api';

const RTV = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const username = localStorage.getItem('username') || 'User';
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [returnQuantity, setReturnQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await productAPI.getAll();
      setProducts(data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load products", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) { navigate('/'); return; }
    loadProducts();
  }, [navigate, loadProducts]);

  const handleLogout = () => { navigate('/dashboard?action=logout'); };

  // Dynamic categories from real products
  const categories = ['All', ...new Set(products.map(p => p.category).filter(Boolean))];

  const filteredProducts = products.filter(p => {
    const matchesCategory = selectedCategory === 'All' || p.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.barcode && p.barcode.includes(searchQuery)) ||
      (p.brand && p.brand.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch && p.stock > 0;
  });

  const selectedProductData = products.find(p => p.id === selectedProduct);

  const handleProcessRTV = async () => {
    if (!selectedProduct || !returnQuantity) {
      toast({ title: "Missing Information", description: "Please select product and enter quantity", variant: "destructive" });
      return;
    }

    const qty = parseInt(returnQuantity);
    if (!selectedProductData || qty > selectedProductData.stock || qty < 1) {
      toast({ title: "Invalid Quantity", description: `Only ${selectedProductData?.stock || 0} units available`, variant: "destructive" });
      return;
    }

    try {
      // Reduce stock via API
      const newStock = selectedProductData.stock - qty;
      await productAPI.update(selectedProduct, { stock: newStock });

      const totalCost = (selectedProductData.cost_price || selectedProductData.costPrice || 0) * qty;

      toast({
        title: "RTV Processed",
        description: `${qty} ${selectedProductData.unit || 'Pcs'} of ${selectedProductData.name} returned. Cost: ₹${totalCost.toFixed(2)}`
      });

      setSelectedProduct('');
      setReturnQuantity('');
      setReason('');
      loadProducts(); // Refresh stock
    } catch (error) {
      toast({ title: "Error", description: "Failed to process RTV", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#e8ecf0' }}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <img src="https://customer-assets.emergentagent.com/job_6a116685-1875-4e0c-97f5-a9213e7bf53a/artifacts/isz5lodv_IMG_6110.PNG" alt="Logo" className="h-10 object-contain" />
            <span className="text-xl font-semibold text-gray-800">Return to Vendor (RTV)</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-600">Welcome, <span className="font-semibold">{username}</span></span>
            <Button onClick={handleLogout} variant="outline" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /><span>Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: RTV Form */}
          <Card className="bg-white lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center space-x-2">
                <PackageX className="w-5 h-5" />
                <span>Process RTV</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Category Filter */}
              <div>
                <Label>Category</Label>
                <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v); setSelectedProduct(''); }} data-testid="rtv-category-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat === 'All' ? 'All Categories' : cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Search */}
              <div>
                <Label>Search Product</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Name, barcode, brand..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="rtv-search"
                  />
                </div>
              </div>

              {/* Product Selection */}
              <div>
                <Label>Select Product</Label>
                <Select value={selectedProduct} onValueChange={setSelectedProduct} data-testid="rtv-product-select">
                  <SelectTrigger><SelectValue placeholder="Choose a product" /></SelectTrigger>
                  <SelectContent>
                    {filteredProducts.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} — Stock: {p.stock} {p.unit || 'Pcs'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Quantity */}
              {selectedProductData && (
                <>
                  <div>
                    <Label>Return Quantity (max {selectedProductData.stock})</Label>
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={returnQuantity}
                      onChange={(e) => setReturnQuantity(e.target.value)}
                      min="1"
                      max={selectedProductData.stock}
                      data-testid="rtv-quantity"
                    />
                  </div>
                  <div>
                    <Label>Reason (optional)</Label>
                    <Input
                      placeholder="e.g. Damaged, Expired, Excess"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      data-testid="rtv-reason"
                    />
                  </div>
                  {returnQuantity && parseInt(returnQuantity) > 0 && (
                    <div className="bg-gray-50 p-3 rounded-lg space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-gray-600">Product</span><span className="font-medium">{selectedProductData.name}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Cost Price</span><span>₹{(selectedProductData.cost_price || 0).toFixed(2)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Quantity</span><span>{returnQuantity} {selectedProductData.unit || 'Pcs'}</span></div>
                      <div className="flex justify-between border-t pt-1 font-semibold"><span>Total Return Value</span><span>₹{((selectedProductData.cost_price || 0) * parseInt(returnQuantity)).toFixed(2)}</span></div>
                    </div>
                  )}
                </>
              )}

              <Button
                onClick={handleProcessRTV}
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold h-11"
                disabled={!selectedProduct || !returnQuantity || parseInt(returnQuantity) < 1}
                data-testid="rtv-process-btn"
              >
                Process RTV
              </Button>
            </CardContent>
          </Card>

          {/* Right: Product Stock List */}
          <Card className="bg-white lg:col-span-2">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Available Stock ({filteredProducts.length} products)</CardTitle>
                <span className="text-sm text-gray-500">{selectedCategory === 'All' ? 'All Categories' : selectedCategory}</span>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center text-gray-500 py-8">Loading products...</p>
              ) : filteredProducts.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No products found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="rtv-stock-table">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Product</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Brand</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Category</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Cost Price</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Stock</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Vendor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map(p => (
                        <tr
                          key={p.id}
                          className={`border-b hover:bg-yellow-50 cursor-pointer ${selectedProduct === p.id ? 'bg-yellow-50' : ''}`}
                          onClick={() => { setSelectedProduct(p.id); setReturnQuantity(''); }}
                          data-testid={`rtv-row-${p.id}`}
                        >
                          <td className="py-2 px-3 font-medium">{p.name}</td>
                          <td className="py-2 px-3 text-gray-600">{p.brand || '-'}</td>
                          <td className="py-2 px-3"><span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs">{p.category}</span></td>
                          <td className="py-2 px-3 text-right">₹{(p.cost_price || 0).toFixed(2)}</td>
                          <td className="py-2 px-3 text-right font-medium">{p.stock} {p.unit || 'Pcs'}</td>
                          <td className="py-2 px-3 text-gray-600">{p.vendor_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RTV;
